/** @format */
import type { Cell } from '@antv/x6'
import { Drawer, Form, FormItem, Input, Icon, Select, Option } from '@king-design/react'
import React, { useContext, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { CountContext } from '..'
import variable from 'src/static/styles/var'
import { cloneDeep } from 'lodash'
import { AddButon, DescriptionItem, Descriptions } from 'src/components'

interface EdgeDrawerProps {
  className?: string
  visible: boolean
  onClose: () => void
  edgeData?: Cell.Properties
  edgeSourceInputs: string[]
  onChange: (values: EdgeFormValuesProsp) => void
}
const EdgeConditionOperatorEnum = ['==', '!=', '=~', '!~', '>', '>=', '<', '<=', 'in']
interface EdgeConditionItemProps {
  Key: string
  Operator: typeof EdgeConditionOperatorEnum[number]
  Value: string
}
const initEdgeConditionItem: EdgeConditionItemProps = {
  Key: '',
  Operator: '==',
  Value: ''
}
interface EdgeFormValuesProsp {
  id?: string
  name: string
  conditions: EdgeConditionItemProps[][]
}
const initEdgeFormValues: EdgeFormValuesProsp = {
  id: '',
  name: '',
  conditions: [[initEdgeConditionItem]]
}

const EdgeDrawer: React.FC<EdgeDrawerProps> = ({
  className,
  visible,
  onClose,
  edgeData,
  edgeSourceInputs,
  onChange
}) => {
  const { editable } = useContext(CountContext)
  const formRef = useRef(null)
  const [formValues, setFormValues] = useState<EdgeFormValuesProsp>(initEdgeFormValues)

  useEffect(() => {
    if (visible && edgeData) {
      setFormValues({
        id: edgeData.id,
        name: edgeData?.data?.name || '',
        conditions: edgeData?.data?.conditions || [[initEdgeConditionItem]]
      })
    } else {
      setFormValues(initEdgeFormValues)
    }
  }, [visible, edgeData])

  const onChangeOr = (
    conditions: EdgeConditionItemProps[],
    orIndex: number,
    move?: 'up' | 'down'
  ) => {
    const copyConditions = cloneDeep(formValues.conditions)
    if (!conditions) {
      // 删除
      copyConditions.splice(orIndex, 1)
    } else if (move === 'up') {
      // 上移
      if (orIndex > 0) {
        const temp = copyConditions[orIndex - 1]
        copyConditions[orIndex - 1] = copyConditions[orIndex]
        copyConditions[orIndex] = temp
      }
    } else if (move === 'down') {
      // 下移
      if (orIndex < copyConditions.length - 1) {
        const temp = copyConditions[orIndex + 1]
        copyConditions[orIndex + 1] = copyConditions[orIndex]
        copyConditions[orIndex] = temp
      }
    } else {
      copyConditions[orIndex] = conditions
    }
    setFormValues({
      ...formValues,
      conditions: copyConditions
    })
  }

  const onChangeAnd = (
    conditions: Partial<EdgeConditionItemProps>,
    orIndex: number,
    andIndex: number
  ) => {
    const copyOrConditions = cloneDeep(formValues.conditions[orIndex])
    if (!conditions) {
      copyOrConditions.splice(andIndex, 1)
    } else {
      copyOrConditions[andIndex] = {
        ...copyOrConditions[andIndex],
        ...conditions
      }
    }
    onChangeOr(copyOrConditions, orIndex)
  }

  return (
    <Drawer
      value={visible}
      width={800}
      title="路由配置"
      mode="destroy"
      container={() => document.querySelector('#scene_detail')}
      onChangeValue={show => {
        if (!show) {
          onClose()
        }
      }}
      onClose={() => {
        onClose()
      }}
      ok={() => {
        onChange(formValues)
      }}
    >
      <div className={className}>
        {editable ? (
          <Form starOnRequired ref={formRef} labelWidth={80}>
            <FormItem label="名称">
              <Input
                width={650}
                value={formValues.name}
                onChangeValue={value => {
                  setFormValues({ ...formValues, name: value })
                }}
              />
            </FormItem>
            <FormItem label="判定条件">
              {formValues.conditions?.map((condition, orIndex) => {
                return (
                  <div className="condition_item" key={orIndex}>
                    <div className="condition_item_header">
                      <span>#{orIndex + 1}.</span>
                      <div className="ksc-space">
                        <Icon
                          className="ion-trash-a"
                          size={16}
                          onClick={() => {
                            onChangeOr(undefined, orIndex)
                          }}
                        />
                        <Icon
                          className="k-icon-up"
                          size={16}
                          onClick={() => onChangeOr(condition, orIndex, 'up')}
                        />
                        <Icon
                          className="k-icon-down"
                          size={16}
                          onClick={() => onChangeOr(condition, orIndex, 'down')}
                        />
                      </div>
                    </div>
                    {condition?.map((item, andIndex) => {
                      const { Key, Operator, Value } = item
                      return (
                        <div className="condition_item_content ksc-space" key={andIndex}>
                          <FormItem hideLabel value={Key} rules={{ required: true }}>
                            <Select
                              width={150}
                              placeholder="KEY"
                              value={Key}
                              onChangeValue={value => {
                                onChangeAnd({ Key: value }, orIndex, andIndex)
                              }}
                            >
                              {edgeSourceInputs?.map(item => (
                                <Option key={item} value={item}>
                                  {item}
                                </Option>
                              ))}
                            </Select>
                          </FormItem>
                          <FormItem hideLabel value={Operator} rules={{ required: true }}>
                            <Select
                              width={80}
                              placeholder="Operator"
                              value={Operator}
                              onChangeValue={value => {
                                onChangeAnd({ Operator: value }, orIndex, andIndex)
                              }}
                            >
                              {EdgeConditionOperatorEnum?.map(item => (
                                <Option key={item} value={item}>
                                  {item}
                                </Option>
                              ))}
                            </Select>
                          </FormItem>
                          <FormItem hideLabel value={Value} rules={{ required: true }}>
                            <Input
                              width={370}
                              placeholder="Value"
                              value={Value}
                              onChangeValue={value => {
                                onChangeAnd({ Value: value }, orIndex, andIndex)
                              }}
                            />
                          </FormItem>
                          <Icon
                            className="ion-trash-a"
                            size={16}
                            onClick={() => {
                              onChangeAnd(undefined, orIndex, andIndex)
                            }}
                          />
                        </div>
                      )
                    })}
                    <div className="add_condition">
                      <AddButon
                        text="并且（Add）"
                        size="small"
                        type="link"
                        fluid={false}
                        onClick={() => {
                          const copyConditions = cloneDeep(formValues.conditions[orIndex])
                          copyConditions.push(initEdgeConditionItem)
                          onChangeOr(copyConditions, orIndex)
                        }}
                      />
                    </div>
                  </div>
                )
              })}
              <div style={{ width: 672 }}>
                <AddButon
                  text="或（Or）"
                  onClick={() => {
                    const copyConditions = cloneDeep(formValues.conditions)
                    copyConditions.push([initEdgeConditionItem])
                    setFormValues({
                      ...formValues,
                      conditions: copyConditions
                    })
                  }}
                />
              </div>
            </FormItem>
          </Form>
        ) : (
          <Descriptions columns={1}>
            <DescriptionItem label="节点名称">{formValues.name}</DescriptionItem>
            <DescriptionItem label="判定条件">
              {formValues.conditions?.map((condition, orIndex) => {
                return (
                  <div className="condition_item" key={orIndex}>
                    <div className="condition_item_header">
                      <span>#{orIndex + 1}.</span>
                    </div>
                    {condition?.map(item => {
                      return (
                        <div className="condition_item_content" key={item.Key}>
                          <p>{item.Key}</p>
                          <p>{item.Operator}</p>
                          <p>{item.Value}</p>
                        </div>
                      )
                    })}
                  </div>
                )
              })}
            </DescriptionItem>
          </Descriptions>
        )}
      </div>
    </Drawer>
  )
}

export default styled(EdgeDrawer)`
  .condition_item {
    width: 672px;
    border: 1px solid #f0f0f0;
    border-radius: 2px;
    margin-bottom: 12px;
    padding-bottom: 8px;
    .condition_item_header {
      display: flex;
      justify-content: space-between;
      padding: 0 12px;
      height: 32px;
      line-height: 32px;
      border-bottom: 1px solid #f0f0f0;
    }
    .condition_item_content {
      padding: 8px 12px;
      display: flex;
      align-items: flex-start;
      p {
        margin-bottom: 0;
        &:nth-child(1) {
          width: 120px;
          word-break: break-all;
          white-space: normal;
        }
        &:nth-child(2) {
          width: 50px;
        }
        &:nth-child(3) {
          width: 500px;
          word-break: break-all;
          white-space: normal;
        }
        &:not(:last-child) {
          margin-right: 4px;
        }
      }
      .k-form-item {
        margin-right: 8px;
      }
      .ion-trash-a {
        width: 20px;
        text-align: center;
        line-height: 32px;
      }
      &:not(:last-child) {
        margin-bottom: 4px;
      }
    }
    .add_condition {
      text-align: right;
    }
    .k-icon {
      cursor: pointer;
      &:hover {
        color: ${variable.primaryColor};
      }
    }
  }
`
